import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import type { AdminIntegrationsDTO } from '@harness/shared';
import { GithubIcon, JiraIcon, PageHeader, Spinner } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { toast } from '../../lib/store';
import { MfaRequired } from '../settings/ServerPage';

// Admin › Integrations: register the GitHub App and the Atlassian OAuth app once. Then everyone gets a
// Connect button (Settings › Connections, project settings) and signs in with their own account.

const key = ['admin', 'integrations'] as const;

function Copyable({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="row" style={{ flexWrap: 'nowrap', gap: 8 }}>
        <input className="input mono" readOnly value={value} onFocus={(e) => e.target.select()} />
        <button className="btn btn-icon btn-secondary" title="Copy" onClick={() => void navigator.clipboard.writeText(value).then(() => toast('Copied'))}>
          <Copy className="icon" />
        </button>
      </div>
    </div>
  );
}

function AppForm({ provider, cfg }: { provider: 'github' | 'atlassian'; cfg: AdminIntegrationsDTO['github'] | AdminIntegrationsDTO['atlassian'] }) {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState(cfg.clientId ?? '');
  const [secret, setSecret] = useState('');
  const [slug, setSlug] = useState(('appSlug' in cfg ? cfg.appSlug : '') ?? '');
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      qc.setQueryData(key, await api.put<AdminIntegrationsDTO>(`/api/admin/integrations/${provider}`, { clientId, clientSecret: secret || undefined, appSlug: provider === 'github' ? slug || null : undefined }));
      setSecret('');
      void qc.invalidateQueries({ queryKey: ['connections'] });
      toast('Saved · people can now click Connect');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm('Remove this app? People keep their saved connections, but they stop refreshing and can’t reconnect until it’s added again.')) return;
    qc.setQueryData(key, await api.del<AdminIntegrationsDTO>(`/api/admin/integrations/${provider}`));
    void qc.invalidateQueries({ queryKey: ['connections'] });
  };
  return (
    <div className="stack-sm">
      <div className="grid-2">
        <div className="field">
          <label htmlFor={`${provider}-cid`}>Client ID</label>
          <input id={`${provider}-cid`} className="input mono" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={provider === 'github' ? 'Iv23li…' : ''} />
        </div>
        <div className="field">
          <label htmlFor={`${provider}-sec`}>Client secret</label>
          <input id={`${provider}-sec`} className="input mono" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg.configured ? '•••••••• (saved, leave empty to keep)' : ''} />
        </div>
      </div>
      {provider === 'github' && (
        <div className="field">
          <label htmlFor="gh-slug">App name in its URL (github.com/apps/…)</label>
          <input id="gh-slug" className="input mono" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="recite-ai-harness" />
          <div className="hint">Used for the “install on your org” link people see if a repo is missing.</div>
        </div>
      )}
      {mfa && <MfaRequired />}
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-primary" disabled={busy || clientId.trim().length < 4 || (!cfg.configured && !secret)} onClick={() => void save()}>
          {cfg.configured ? 'Save' : 'Add app'}
        </button>
        {cfg.configured && (
          <button className="btn btn-ghost" onClick={() => void remove()}>
            Remove
          </button>
        )}
        {cfg.configured && <span className="muted" style={{ fontSize: 13 }}>{cfg.connectedPeople} {cfg.connectedPeople === 1 ? 'person has' : 'people have'} connected</span>}
      </div>
    </div>
  );
}

export default function IntegrationsAdminPage() {
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminIntegrationsDTO>('/api/admin/integrations') });
  if (!q.data) return q.isLoading ? <Spinner /> : <div className="banner">{errorText(q.error)}</div>;
  const d = q.data;
  return (
    <div className="settings-content">
      <PageHeader title="Integrations" sub="Register the GitHub and Atlassian apps once. Everyone then connects their own account with one click; no tokens to paste." />

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <GithubIcon /> GitHub App {d.github.configured && <span className="tag tag-done sm">Ready</span>}
        </div>
        <ol className="muted" style={{ fontSize: 14, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
          <li>
            On GitHub: your organisation (ReciteMe) › Settings › Developer settings › GitHub Apps › <b>New GitHub App</b>.
          </li>
          <li>
            Callback URL: the one below. Leave “Expire user authorization tokens” <b>on</b>. Webhook: off (not needed).
          </li>
          <li>
            Repository permissions: <b>Contents</b> read &amp; write, <b>Pull requests</b> read &amp; write, <b>Metadata</b> read. Nothing else.
          </li>
          <li>Create it, generate a <b>client secret</b>, then <b>Install</b> it on the org and choose the repos the harness may use.</li>
          <li>Paste the Client ID, client secret and the app’s URL name here.</li>
        </ol>
        <Copyable label="Callback URL" value={d.github.callbackUrl} />
        <AppForm provider="github" cfg={d.github} />
      </section>

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <JiraIcon /> Atlassian (Jira) {d.atlassian.configured && <span className="tag tag-done sm">Ready</span>}
        </div>
        <ol className="muted" style={{ fontSize: 14, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
          <li>
            On developer.atlassian.com › Console › <b>Create › OAuth 2.0 integration</b>.
          </li>
          <li>
            Permissions › Jira API › add scopes <b>read:jira-work</b>, <b>write:jira-work</b>, <b>read:jira-user</b>.
          </li>
          <li>Authorization › OAuth 2.0 (3LO) › Callback URL: the one below.</li>
          <li>Settings: copy the Client ID and secret here. (Distribution can stay private while only Recite Me uses it.)</li>
        </ol>
        <Copyable label="Callback URL" value={d.atlassian.callbackUrl} />
        <AppForm provider="atlassian" cfg={d.atlassian} />
      </section>
    </div>
  );
}
